import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider as MuiDivider,
  IconButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fi } from "date-fns/locale";
import { format } from "date-fns";
import useAuth from "../../../hooks/useAuth";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import useSnackbar from "../SnackbarHook";
import CustomSnackbar from "../SnackBar";
import { toDateOnlyString } from "../../../utils/dateUtils";
import {
  approveBudget,
  fetchBudgetFiscalYears,
  fetchBudgetReport,
  saveBudgetLines,
  unapproveBudget,
} from "../../../redux/slices/budget";
import {
  amountsFromPreviousActual,
  buildBudgetLines,
  formatInputAmount,
  parseAmount,
  toBudgetLines,
} from "./budgetTable";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const euro = (value: number | null) =>
  value == null
    ? "–"
    : new Intl.NumberFormat("fi-FI", {
        style: "currency",
        currency: "EUR",
      }).format(value);

const formatDate = (value: string | null | undefined) =>
  value ? format(new Date(value), "d.M.yyyy") : "";

const periodLabel = (period: { from: string; to: string } | null) =>
  period ? `${formatDate(period.from)}–${formatDate(period.to)}` : "";

function Budget() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const associationId: number =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const { fiscalYears, report, status, saving } = useAppSelector(
    (state) => state.budget
  );

  const [fiscalYearId, setFiscalYearId] = useState<number | "">("");
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [dirty, setDirty] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [approveOpen, setApproveOpen] = useState(false);
  const [approvedAt, setApprovedAt] = useState<Date | null>(new Date());

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
  } = useSnackbar();

  const notify = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setProgress(100);
    setOpenSnackbar(true);
  };

  useEffect(() => {
    if (associationId) dispatch(fetchBudgetFiscalYears(associationId));
  }, [dispatch, associationId]);

  // Oletuksena nykyinen tilikausi, muuten viimeisin
  useEffect(() => {
    if (fiscalYearId !== "" || fiscalYears.length === 0) return;
    const active =
      fiscalYears.find((fy) => fy.active) ??
      [...fiscalYears].sort((a, b) => b.from.localeCompare(a.from))[0];
    setFiscalYearId(active.id);
  }, [fiscalYears, fiscalYearId]);

  useEffect(() => {
    if (!associationId || fiscalYearId === "") return;
    dispatch(fetchBudgetReport({ associationId, fiscalYearId }));
  }, [dispatch, associationId, fiscalYearId]);

  // Syötteet talousarvion tallennetuista riveistä aina kun raportti vaihtuu
  useEffect(() => {
    const next: Record<number, string> = {};
    for (const line of report?.lines ?? []) {
      next[line.accountCode] = formatInputAmount(Number(line.amount));
    }
    setAmounts(next);
    setDirty(false);
  }, [report?.lines]);

  const isApproved = report?.budget?.status === "approved";

  const lines = useMemo(
    () =>
      report
        ? buildBudgetLines(report.actual, report.previousActual, amounts)
        : [],
    [report, amounts]
  );

  // Row ids are dotted paths ("3.1.1.2"), so a collapsed id hides everything
  // below it: its sub-groups and its own accounts, but not its heading.
  const visibleLines = lines.filter(
    (line) =>
      !line.isTotal &&
      !Array.from(collapsed).some(
        (id) =>
          line.groupId.startsWith(`${id}.`) ||
          (line.isAccount && line.groupId === id)
      )
  );
  const totalLine = lines.find((line) => line.isTotal);

  const toggle = (id: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setAmount = (code: number, value: string) => {
    setAmounts((previous) => ({ ...previous, [code]: value }));
    setDirty(true);
  };

  const reload = () => {
    if (fiscalYearId !== "") {
      dispatch(fetchBudgetReport({ associationId, fiscalYearId }));
    }
  };

  const handleSave = async () => {
    if (fiscalYearId === "") return;
    const { lines: toSave, invalid } = toBudgetLines(amounts);
    if (invalid.length > 0) {
      notify(
        `Tarkista summat tileillä ${invalid.join(
          ", "
        )}: positiivinen luku, enintään kaksi desimaalia.`,
        "error"
      );
      return;
    }
    try {
      await dispatch(
        saveBudgetLines({ associationId, fiscalYearId, lines: toSave })
      ).unwrap();
      notify("Talousarvio tallennettu.", "success");
      reload();
    } catch (error: any) {
      notify(error?.message ?? "Talousarvion tallennus epäonnistui.", "error");
    }
  };

  const handlePrefill = () => {
    if (!report?.previousActual) return;
    setAmounts(amountsFromPreviousActual(report.previousActual));
    setDirty(true);
  };

  const handleApprove = async () => {
    const date = toDateOnlyString(approvedAt);
    if (!report?.budget || !date) return;
    try {
      await dispatch(
        approveBudget({ budgetId: report.budget.id, approvedAt: date })
      ).unwrap();
      setApproveOpen(false);
      notify("Talousarvio merkitty hyväksytyksi.", "success");
    } catch (error: any) {
      notify(
        error?.message ?? "Hyväksytyksi merkitseminen epäonnistui.",
        "error"
      );
    }
  };

  const handleUnapprove = async () => {
    if (!report?.budget) return;
    try {
      await dispatch(unapproveBudget(report.budget.id)).unwrap();
      notify("Talousarvio palautettu luonnokseksi.", "success");
    } catch (error: any) {
      notify(
        error?.message ?? "Luonnokseksi palauttaminen epäonnistui.",
        "error"
      );
    }
  };

  const hasSavedLines = (report?.lines.length ?? 0) > 0;

  return (
    <React.Fragment>
      <Helmet title="Talousarvio" />
      <Typography variant="h3" gutterBottom display="inline">
        Talousarvio
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Talousarvio</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Card mb={6}>
        <CardContent>
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            gap={2}
            mb={3}
          >
            <TextField
              select
              size="small"
              label="Tilikausi"
              value={fiscalYearId}
              onChange={(event) => {
                if (
                  dirty &&
                  !window.confirm(
                    "Tallentamattomat muutokset menetetään. Vaihdetaanko tilikautta?"
                  )
                ) {
                  return;
                }
                setFiscalYearId(Number(event.target.value));
              }}
              sx={{ minWidth: 220 }}
            >
              {[...fiscalYears]
                .sort((a, b) => b.from.localeCompare(a.from))
                .map((fy) => (
                  <MenuItem key={fy.id} value={fy.id}>
                    {periodLabel(fy)}
                    {fy.active ? " (nykyinen)" : ""}
                  </MenuItem>
                ))}
            </TextField>

            {report &&
              (isApproved ? (
                <Chip
                  color="success"
                  label={`Hyväksytty ${formatDate(report.budget?.approvedAt)}`}
                />
              ) : (
                <Chip
                  label={report.budget ? "Luonnos" : "Ei vielä talousarviota"}
                />
              ))}

            <Box flexGrow={1} />

            {!isApproved && (
              <>
                <Button
                  variant="outlined"
                  onClick={handlePrefill}
                  disabled={!report?.previousActual}
                  title={
                    report?.previousActual
                      ? `Toteuma ${periodLabel(report.prevPeriod)}`
                      : "Edellistä tilikautta ei ole"
                  }
                >
                  Täytä edellisen vuoden toteumalla
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setApproveOpen(true)}
                  disabled={!report?.budget || !hasSavedLines || dirty}
                  title={
                    dirty ? "Tallenna muutokset ennen hyväksymistä" : undefined
                  }
                >
                  Merkitse hyväksytyksi
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!report || saving}
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  Tallenna
                </Button>
              </>
            )}
            {isApproved && (
              <Button variant="outlined" onClick={handleUnapprove}>
                Palauta luonnokseksi
              </Button>
            )}
          </Box>

          {isApproved && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Tiekunnan kokouksen hyväksymää talousarviota ei voi muuttaa.
              Palauta se luonnokseksi, jos kokous muutti lukuja.
            </Alert>
          )}
          {dirty && !isApproved && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Muutoksia ei ole tallennettu.
            </Alert>
          )}

          {status === "loading" && !report ? (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress />
            </Box>
          ) : status === "failed" ? (
            <Alert severity="error">Talousarvion haku epäonnistui.</Alert>
          ) : report ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tili</TableCell>
                    <TableCell align="right">Talousarvio</TableCell>
                    <TableCell align="right">Toteuma</TableCell>
                    <TableCell
                      align="right"
                      title="Positiivinen, kun toteuma on talousarviota parempi"
                    >
                      Erotus
                    </TableCell>
                    <TableCell align="right">
                      Toteuma {periodLabel(report.prevPeriod)}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleLines.map((line) => (
                    <TableRow key={line.key}>
                      <TableCell
                        sx={{
                          pl: `${16 + line.depth * 16}px`,
                          fontWeight: line.isAccount ? "normal" : "bold",
                        }}
                      >
                        {!line.isAccount && (
                          <IconButton
                            size="small"
                            onClick={() => toggle(line.groupId)}
                            sx={{ mr: 0.5 }}
                          >
                            {collapsed.has(line.groupId) ? (
                              <ExpandMore fontSize="small" />
                            ) : (
                              <ExpandLess fontSize="small" />
                            )}
                          </IconButton>
                        )}
                        {line.label}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: line.isAccount ? "normal" : "bold" }}
                      >
                        {line.isAccount && line.code != null ? (
                          <TextField
                            size="small"
                            value={amounts[line.code] ?? ""}
                            onChange={(event) =>
                              setAmount(line.code!, event.target.value)
                            }
                            disabled={isApproved}
                            error={Number.isNaN(
                              parseAmount(amounts[line.code])
                            )}
                            placeholder="0,00"
                            inputProps={{
                              inputMode: "decimal",
                              style: { textAlign: "right" },
                              "aria-label": `Talousarvio ${line.label}`,
                            }}
                            sx={{ width: 130 }}
                          />
                        ) : (
                          euro(line.budget)
                        )}
                      </TableCell>
                      <TableCell align="right">{euro(line.actual)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color:
                            line.difference < -0.004
                              ? "error.main"
                              : line.difference > 0.004
                              ? "success.main"
                              : undefined,
                        }}
                      >
                        {euro(line.difference)}
                      </TableCell>
                      <TableCell align="right">{euro(line.previous)}</TableCell>
                    </TableRow>
                  ))}
                  {totalLine && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {totalLine.label}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {euro(totalLine.budget)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {euro(totalLine.actual)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {euro(totalLine.difference)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {euro(totalLine.previous)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)}>
        <DialogTitle>Merkitse talousarvio hyväksytyksi</DialogTitle>
        <DialogContent>
          <Typography mb={3}>
            Anna päivä, jolloin tiekunnan kokous hyväksyi talousarvion.
            Hyväksyttyä talousarviota ei voi muuttaa ennen kuin se palautetaan
            luonnokseksi.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DatePicker
              label="Hyväksytty kokouksessa"
              value={approvedAt}
              inputFormat="dd.MM.yyyy"
              onChange={(value: Date | null) => setApprovedAt(value)}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Peruuta</Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={!approvedAt}
          >
            Merkitse hyväksytyksi
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        onClose={() => setOpenSnackbar(false)}
      />
    </React.Fragment>
  );
}

export default Budget;
